import React, { useState, useEffect, useRef } from 'react';
import {
  Phone, MapPin, Info, ArrowClockwise, Check, X, ArrowRight, CheckCircle,
} from '@phosphor-icons/react';
import {
  getBranchNames as fetchBranchNames, getGroupCode as fetchGroupCode, getTickets as fetchTickets,
  getSubscriberNames, getSubscriberNameDetails, getAddressDetails,
  UpdateMoblieNoByContact, UpdateAddressByContact, saveData as saveAuditLog,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import SectionLock from './SectionLock';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult, inlineNoticeColor } from '../utils/noData';

const onlyDigits = (value, max) => value.replace(/[^0-9]/g, '').slice(0, max);

const INDIAN_MOBILE = /^[6-9]\d{9}$/;

const TH = { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap' };
const TD = { padding: '8px 10px', borderBottom: '1px solid #F3F4F6', color: '#111827', fontSize: '12px', whiteSpace: 'nowrap' };

// A row is identified by the chit it represents, not by its array index.
const chitKey = (row) => `${row.contact_id}-${row.groupcode}-${row.ticketno}`;

const mobileErrorFor = (value) => {
  if (!value) return 'Mobile number is required';
  if (value.length < 10) return `Mobile number must be 10 digits — ${value.length} of 10 entered`;
  if (!INDIAN_MOBILE.test(value)) return 'An Indian mobile number must start with 6, 7, 8 or 9';
  return null;
};

function ChangeTypeCard({ active, icon, title, desc, onClick, testId }) {
  return (
    <div
      onClick={onClick}
      data-testid={testId}
      style={{
        display: 'flex', alignItems: 'center', gap: '14px', padding: '16px', borderRadius: '10px',
        border: `2px solid ${active ? '#002FA7' : '#E5E7EB'}`, background: active ? '#EFF4FF' : 'white',
        cursor: 'pointer', position: 'relative', flex: 1,
      }}
    >
      <div style={{
        width: '38px', height: '38px', borderRadius: '8px', background: active ? '#002FA7' : '#F3F4F6',
        color: active ? 'white' : '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}
      >
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>{title}</div>
        <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{desc}</div>
      </div>
      {active && <CheckCircle size={18} color="#002FA7" weight="fill" />}
    </div>
  );
}

function ChangeMobileAddressForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchlist, setBranchlist] = useState([]);
  const [branchCodeList, setBranchCodeList] = useState([]); // groups
  const [tickets, setTickets] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');

  const [branchschema, setBranchschema] = useState(null);
  const [groupCode, setGroupCode] = useState(null);

  const [contactId, setContactId] = useState(null);
  const [oldName, setOldName] = useState('');
  const [oldMobileNo, setOldMobileNo] = useState('');
  const [oldAddress, setOldAddress] = useState('');
  const [oldArea, setOldArea] = useState('');
  const [oldCity, setOldCity] = useState('');
  const [oldPincode, setOldPincode] = useState('');
  const [oldStatus, setOldStatus] = useState('');

  // Every chit the ticket's subscriber holds. One tick at a time (the checkbox
  // column behaves as a radio, as on Change Name); ticking is what loads the
  // current details below.
  const [chitRows, setChitRows] = useState([]);
  const [chitsLoading, setChitsLoading] = useState(false);
  const [chitsError, setChitsError] = useState('');
  const [selectedChitKey, setSelectedChitKey] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  // Tickets and ticks can change faster than the lookups return; only the latest
  // request is allowed to write its results.
  const loadRef = useRef(0);

  const [changeType, setChangeType] = useState(''); // 'MOBILE' | 'ADDRESS'

  const [newMobileNo, setNewMobileNo] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [newArea, setNewArea] = useState('');
  const [newCity, setNewCity] = useState('');
  const [newPincode, setNewPincode] = useState('');

  const [reason, setReason] = useState('');

  const [touched, setTouched] = useState(false);
  // The form's `touched` only flips on Save, which left a mistyped mobile number
  // unflagged until then. This field tracks its own blur so the error appears as
  // soon as the user leaves the box.
  const [mobileTouched, setMobileTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState(null);
  const [pendingAudit, setPendingAudit] = useState(null);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    fetchBranchNames().then((res) => setBranchlist(res || [])).catch(() => setBranchlist([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const showToast = (severity, summary, detail, sticky = false) => {
    setToast({ severity, summary, detail, sticky });
    if (!sticky) setTimeout(() => setToast(null), 4000);
  };

  const resetNewValues = () => {
    setNewMobileNo(''); setNewAddress(''); setNewArea(''); setNewCity(''); setNewPincode('');
    setMobileTouched(false);
  };

  const clearCurrentDetails = () => {
    loadRef.current += 1;
    setDetailsLoading(false);
    setContactId(null);
    setOldName(''); setOldMobileNo(''); setOldAddress(''); setOldArea(''); setOldCity(''); setOldPincode(''); setOldStatus('');
    setChangeType('');
    resetNewValues();
  };

  const resetFromTicket = () => {
    clearCurrentDetails();
    setChitRows([]);
    setChitsLoading(false);
    setChitsError('');
    setSelectedChitKey(null);
  };

  const resetFromGroup = () => {
    setGroupCode(null);
    setTicket('');
    resetFromTicket();
    setTickets([]);
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setGroup('');
    resetFromGroup();
    setBranchCodeList([]);
    setTickets([]);
  };

  const loadGroups = async (branch) => {
    const schema = branch.branch_code;
    setBranchschema(schema);
    try {
      const res = await fetchGroupCode(schema);
      setBranchCodeList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setBranchCodeList([]);
    }
  };

  const loadTickets = async (schema, gCode) => {
    try {
      const res = await fetchTickets(schema, gCode);
      setTickets(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTickets([]);
    }
  };

  const handleCaoChange = (value) => {
    setCao(value || '');
    resetFromBranch();
    if (!value) return;
    const selected = branchlist.find((x) => x.branch_name === value);
    if (selected) loadGroups(selected);
  };

  const handleGroupChange = (value) => {
    setGroup(value || '');
    resetFromGroup();
    if (!value) return;
    const selected = branchCodeList.find((x) => x.group_Code === value);
    if (selected) {
      setGroupCode(selected.group_Code);
      loadTickets(branchschema, selected.group_Code);
    }
  };

  const handleTicketChange = async (value) => {
    setTicket(value || '');
    resetFromTicket();
    if (!value || !groupCode) return;

    // getnames resolves the subscriber behind the ticket; its contact id then
    // drives getnamesdetials, which lists every chit that contact holds.
    const load = loadRef.current;
    setChitsLoading(true);
    try {
      const names = await getSubscriberNames(branchschema, groupCode, value);
      if (load !== loadRef.current) return;
      const first = Array.isArray(names) ? names[0] : null;
      if (!first?.contact_id) {
        setChitsError(NO_DATA_MESSAGE);
        return;
      }
      const details = await getSubscriberNameDetails(first.contact_id);
      if (load !== loadRef.current) return;
      setChitRows(Array.isArray(details) ? details : []);
      // Inline next to the empty grid, as on Change Name.
      if (isEmptyResult(details)) setChitsError(NO_DATA_MESSAGE);
    } catch (err) {
      if (load !== loadRef.current) return;
      console.error('Subscriber lookup failed:', err);
      setChitsError('Could not load the subscriber details for that ticket.');
    } finally {
      if (load === loadRef.current) setChitsLoading(false);
    }
  };

  const handleChitSelect = async (row) => {
    const key = chitKey(row);
    // Ticking the ticked row again clears it; ticking another replaces it.
    const next = selectedChitKey === key ? null : key;
    setSelectedChitKey(next);
    clearCurrentDetails();
    if (!next) return;

    const load = loadRef.current;
    setDetailsLoading(true);
    try {
      const rows = await getAddressDetails(row.contact_id);
      if (load !== loadRef.current) return;
      const address = Array.isArray(rows) ? rows[0] : null;
      if (!address) {
        showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
        return;
      }
      setContactId(row.contact_id);
      setOldName(address.contactMailingName || row.subscriber_name || '');
      setOldMobileNo(address.phoneNo || '');
      setOldAddress(address.address || '');
      setOldArea(address.area || '');
      setOldCity(address.city || '');
      setOldPincode(address.pin || '');
      setOldStatus(row.chit_status || '');
    } catch (err) {
      if (load !== loadRef.current) return;
      console.error('getaddressdetails failed:', err);
      showToast('error', 'Load Failed', 'Could not load the address details for that record.');
    } finally {
      if (load === loadRef.current) setDetailsLoading(false);
    }
  };

  const selectedChit = chitRows.find((r) => chitKey(r) === selectedChitKey) || null;

  const handleChangeType = (type) => {
    if (!ticket) {
      showToast('warn', 'Selection Required', 'Please select Branch (CAO), Group Code and Ticket No first.');
      return;
    }
    if (!selectedChit || !contactId) {
      showToast('warn', 'Selection Required', 'Please select a record in the grid first.');
      return;
    }
    setChangeType(type);
    resetNewValues();
  };

  const isMobileChange = changeType === 'MOBILE';
  const isAddressChange = changeType === 'ADDRESS';

  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && !ticket;
  const changeTypeInvalid = touched && !changeType;
  const mobileError = isMobileChange ? mobileErrorFor(newMobileNo) : null;
  const mobileInvalid = (touched || mobileTouched) && isMobileChange && !!mobileError;
  const addressInvalid = touched && isAddressChange && !newAddress.trim();
  const areaInvalid = touched && isAddressChange && !newArea.trim();
  const cityInvalid = touched && isAddressChange && !newCity.trim();
  const pincodeInvalid = touched && isAddressChange && !/^[1-9][0-9]{5}$/.test(newPincode);
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const isValid = !!selectedChit && !!contactId && !!changeType
    && (isMobileChange ? !mobileError : true)
    && (isAddressChange ? (
      newAddress.trim().length > 0 && newAddress.length <= 100
      && newArea.trim().length > 0 && newArea.length <= 50
      && newCity.trim().length > 0 && newCity.length <= 50
      && /^[1-9][0-9]{5}$/.test(newPincode)
    ) : true)
    && reason.trim().length >= 10 && reason.length <= 200;

  const confirmSave = () => {
    setTouched(true);

    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    let updatePayload = { contact_id: contactId, ptypeofoperation: 'UPDATE' };
    let changes = [];

    if (isMobileChange) {
      updatePayload = { ...updatePayload, newMobileNo };
      changes = [
        { label: 'Mobile No.', oldValue: oldMobileNo || '', newValue: newMobileNo || '' },
      ];
    } else {
      updatePayload = {
        ...updatePayload, newAddress, newArea, newCity, newPincode,
      };
      changes = [
        { label: 'Address', oldValue: oldAddress || '', newValue: newAddress || '' },
        { label: 'Area', oldValue: oldArea || '', newValue: newArea || '' },
        { label: 'City', oldValue: oldCity || '', newValue: newCity || '' },
        { label: 'Pincode', oldValue: oldPincode || '', newValue: newPincode || '' },
      ];
    }

    setPendingChanges(changes);
    setPendingReason(reason);
    setPendingUpdatePayload(updatePayload);

    setPendingAudit({
      schemaName: branchschema,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: isMobileChange ? 'MOBILE_CHANGE' : 'ADDRESS_CHANGE',
      oldData: isMobileChange
        ? { mobileNo: oldMobileNo, status: oldStatus }
        : { address: oldAddress, area: oldArea, city: oldCity, pincode: oldPincode, status: oldStatus },
      newData: isMobileChange
        ? { mobileNo: newMobileNo }
        : { address: newAddress, area: newArea, city: newCity, pincode: newPincode },
      reason,
    });

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
    setPendingUpdatePayload(null);
    setPendingAudit(null);
    setPendingChanges([]);
  };

  const doSave = async () => {
    if (!pendingUpdatePayload || !pendingAudit) return;

    setSaving(true);
    try {
      if (isMobileChange) {
        await UpdateMoblieNoByContact(pendingUpdatePayload);
      } else {
        await UpdateAddressByContact(pendingUpdatePayload);
      }
      try {
        await saveAuditLog([pendingAudit]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Successfully updated and audit saved.', true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Updated, but audit log failed to save.', true);
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('Update failed:', err);
      // Surface what the server actually said. A bad Connection2 in the
      // Connection Registry reports as `database "X" does not exist`, which
      // "Please try again" hid behind a retry that could never succeed.
      showToast('error', 'Update Failed', apiErrorMessage(err, 'Could not update the record. Please try again.'), true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setReason('');
    setTouched(false);
    setMobileTouched(false);
    setPendingUpdatePayload(null);
    setPendingAudit(null);
    setPendingChanges([]);
    setPendingReason('');
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="change-mobileaddress-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="change-mobileaddress-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Phone size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Change Mobile No &amp; Address</div>
          </div>
        </div>

        {/* SECTION A: Select Ticket */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group and ticket to load member details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleCaoChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="change-mobileaddress-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={branchCodeList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a branch first"
              disabled={!cao}
              error={groupInvalid}
              testId="change-mobileaddress-group-select"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket no"
              emptyMessage="Select a group first"
              disabled={!group}
              error={ticketInvalid}
              testId="change-mobileaddress-ticket-select"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        {/* Every chit the ticket's subscriber holds — the grid carries the
            subscriber name. Ticking one is what fills in the current details below. */}
        {ticket && (
          <div style={{ marginTop: '4px' }}>
            {chitsError && (
              <div style={{ fontSize: '11px', color: inlineNoticeColor(chitsError), marginTop: '12px' }} data-testid="change-mobileaddress-chits-error">
                {chitsError}
              </div>
            )}

            {!chitsError && (
              <div style={{ marginTop: '12px', border: '1px solid #E5E7EB', borderRadius: '8px', overflowX: 'auto', width: 0, minWidth: '100%' }}>
                <table style={{ width: '100%', minWidth: '620px', borderCollapse: 'collapse' }} data-testid="change-mobileaddress-chits-table">
                  <thead style={{ background: '#F9FAFB' }}>
                    <tr>
                      <th style={{ ...TH, width: '44px' }}>Select</th>
                      <th style={TH}>Contact ID</th>
                      <th style={TH}>Subscriber Name</th>
                      <th style={TH}>Group Code</th>
                      <th style={TH}>Ticket No</th>
                      <th style={TH}>Chit Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chitsLoading && (
                      <tr>
                        <td colSpan={6} style={{ ...TD, textAlign: 'center', color: '#9CA3AF' }}>Loading subscriber details...</td>
                      </tr>
                    )}
                    {!chitsLoading && chitRows.map((row) => {
                      const key = chitKey(row);
                      const checked = selectedChitKey === key;
                      return (
                        <tr
                          key={key}
                          style={{ background: checked ? '#EFF6FF' : 'white', cursor: 'pointer' }}
                          onClick={() => handleChitSelect(row)}
                          data-testid={`change-mobileaddress-chit-row-${row.ticketno}`}
                        >
                          <td style={TD}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => handleChitSelect(row)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '15px', height: '15px', accentColor: '#002FA7', cursor: 'pointer' }}
                              data-testid={`change-mobileaddress-chit-check-${row.ticketno}`}
                            />
                          </td>
                          <td style={TD}>{row.contact_id}</td>
                          <td style={TD}>{row.subscriber_name}</td>
                          <td style={TD}>{row.groupcode}</td>
                          <td style={TD}>{row.ticketno}</td>
                          <td style={TD}>{row.chit_status}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!chitsLoading && chitRows.length > 0 && !selectedChit && (
              <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '6px' }}>
                Select one record to load its current details below.
              </div>
            )}
            {detailsLoading && (
              <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '6px' }}>Loading address details...</div>
            )}
          </div>
        )}

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        {/* SECTION B: Current Details */}
        <SectionLock locked={!contactId} hint="Select a ticket and tick a record in section A first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Current Details</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Read-only details of the record selected in the grid above</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '4px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Name</label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={oldName} readOnly data-testid="change-mobileaddress-old-name" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Mobile</label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={oldMobileNo} readOnly data-testid="change-mobileaddress-old-mobile" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Address</label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={oldAddress} readOnly data-testid="change-mobileaddress-old-address" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Area</label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={oldArea} readOnly data-testid="change-mobileaddress-old-area" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>City</label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={oldCity} readOnly data-testid="change-mobileaddress-old-city" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Pincode</label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={oldPincode} readOnly data-testid="change-mobileaddress-old-pincode" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Status</label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={oldStatus} readOnly data-testid="change-mobileaddress-old-status" />
          </div>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        {/* SECTION C: Type of Change */}
        <SectionLock locked={!contactId} hint="Select a ticket and tick a record in section A first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Type of Change</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Select whether you&apos;re updating the mobile number or the address</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px' }}>
          <ChangeTypeCard
            active={isMobileChange}
            icon={<Phone size={18} />}
            title="Mobile Number"
            desc="Update the registered mobile number"
            onClick={() => handleChangeType('MOBILE')}
            testId="change-mobileaddress-type-mobile"
          />
          <ChangeTypeCard
            active={isAddressChange}
            icon={<MapPin size={18} />}
            title="Address"
            desc="Update address, area, city and pincode"
            onClick={() => handleChangeType('ADDRESS')}
            testId="change-mobileaddress-type-address"
          />
        </div>
        {changeTypeInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '6px' }}>Please select change type</div>}
        </SectionLock>

        {isMobileChange && (
          <>
            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>D</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>New Mobile Number</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Enter the new 10-digit mobile number</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>
                    New Mobile No. <span style={{ color: '#DC2626' }}>*</span>
                  </label>
                  <span style={{ fontSize: '11px', color: newMobileNo.length === 10 ? '#059669' : '#9CA3AF' }}>
                    {newMobileNo.length} / 10
                  </span>
                </div>
                <input
                  className="input-field"
                  style={{ fontSize: '13px', borderColor: mobileInvalid ? '#DC2626' : undefined }}
                  value={newMobileNo}
                  maxLength={10}
                  inputMode="numeric"
                  placeholder="Enter new mobile number"
                  onChange={(e) => setNewMobileNo(onlyDigits(e.target.value, 10))}
                  onBlur={() => setMobileTouched(true)}
                  data-testid="change-mobileaddress-new-mobile"
                />
                {mobileInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>{mobileError}</div>}
              </div>
            </div>
          </>
        )}

        {isAddressChange && (
          <>
            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>D</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>New Address</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Enter the updated address details</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                  New Address <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <input
                  className="input-field"
                  style={{ fontSize: '13px', borderColor: addressInvalid ? '#DC2626' : undefined }}
                  value={newAddress}
                  maxLength={100}
                  placeholder="Enter new address"
                  onChange={(e) => setNewAddress(e.target.value)}
                  data-testid="change-mobileaddress-new-address"
                />
                {addressInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Address is required</div>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                  New Area <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <input
                  className="input-field"
                  style={{ fontSize: '13px', borderColor: areaInvalid ? '#DC2626' : undefined }}
                  value={newArea}
                  maxLength={50}
                  placeholder="Enter new area"
                  onChange={(e) => setNewArea(e.target.value)}
                  data-testid="change-mobileaddress-new-area"
                />
                {areaInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Area is required</div>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                  New City <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <input
                  className="input-field"
                  style={{ fontSize: '13px', borderColor: cityInvalid ? '#DC2626' : undefined }}
                  value={newCity}
                  maxLength={50}
                  placeholder="Enter new city"
                  onChange={(e) => setNewCity(e.target.value)}
                  data-testid="change-mobileaddress-new-city"
                />
                {cityInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>City is required</div>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                  New Pincode <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <input
                  className="input-field"
                  style={{ fontSize: '13px', borderColor: pincodeInvalid ? '#DC2626' : undefined }}
                  value={newPincode}
                  maxLength={6}
                  placeholder="Enter new pincode"
                  onChange={(e) => setNewPincode(onlyDigits(e.target.value, 6))}
                  data-testid="change-mobileaddress-new-pincode"
                />
                {pincodeInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Enter a valid 6-digit pincode</div>}
              </div>
            </div>
          </>
        )}

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        {/* SECTION E: Reason */}
        <SectionLock locked={!contactId || !changeType} hint="Select the type of change in section C first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>E</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Change</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Explain why this change is being made..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="change-mobileaddress-reason"
        />
        {reasonInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Minimum 10 characters required</div>}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="change-mobileaddress-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={confirmSave} className="btn-primary" disabled={saving} data-testid="change-mobileaddress-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="change-mobileaddress-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Phone size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                  Confirm {isMobileChange ? 'Mobile Number' : 'Address'} Change
                </h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to update the {isMobileChange ? 'mobile number' : 'address'} for this subscriber. Please review before confirming.
              </p>

              {pendingChanges.map((change) => (
                <div key={change.label} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>{change.label} (Old)</p>
                    <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{change.oldValue}</p>
                  </div>
                  <ArrowRight size={14} color="#9CA3AF" />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>{change.label} (New)</p>
                    <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{change.newValue}</p>
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="change-mobileaddress-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={doSave} disabled={saving} className="btn-primary" data-testid="change-mobileaddress-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Yes, Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChangeMobileAddressForm;
